import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSend, FiSave, FiChevronDown, FiChevronUp, FiTrash2, FiPaperclip, FiFile } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { sendEmail, createDraft, updateDraft, deleteDraft, sendDraft } from '../api/emailApi';
import { useStorageReadOnly } from '../hooks/useStorageReadOnly';

interface EmailComposeProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    draftId?: string;
    to?: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject?: string;
    bodyHtml?: string;
    bodyText?: string;
    threadId?: string;
    replyTo?: string;
  };
  onSent?: () => void;
}

export default function EmailCompose({ isOpen, onClose, initialData, onSent }: EmailComposeProps) {
  const [to, setTo] = useState<string>('');
  const [cc, setCc] = useState<string>('');
  const [bcc, setBcc] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [bodyHtml, setBodyHtml] = useState<string>('');
  const [showCc, setShowCc] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const { isReadOnly } = useStorageReadOnly();

  // Initialize form with initialData
  useEffect(() => {
    if (isOpen && initialData) {
      setTo(Array.isArray(initialData.to) ? initialData.to.join(', ') : (initialData.to || ''));
      setCc(Array.isArray(initialData.cc) ? initialData.cc.join(', ') : (initialData.cc || ''));
      setBcc(Array.isArray(initialData.bcc) ? initialData.bcc.join(', ') : (initialData.bcc || ''));
      setSubject(initialData.subject || '');
      const initialBody = initialData.bodyHtml || initialData.bodyText || '';
      setBodyHtml(initialBody);
      setDraftId(initialData.draftId || null);
      setShowCc(!!initialData.cc || !!initialData.bcc);
      setAttachments([]); // Reset attachments when loading draft
      
      // Update contentEditable div content when loading initial data
      if (isHtmlMode && bodyRef.current && initialBody) {
        isInternalUpdate.current = true;
        bodyRef.current.innerHTML = initialBody;
        // Reset cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(bodyRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
        setTimeout(() => {
          isInternalUpdate.current = false;
        }, 0);
      }
    } else if (isOpen) {
      // Reset form when opening new compose
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBodyHtml('');
      setDraftId(null);
      setShowCc(false);
      
      // Clear contentEditable
      if (bodyRef.current) {
        isInternalUpdate.current = true;
        bodyRef.current.innerHTML = '';
        setTimeout(() => {
          isInternalUpdate.current = false;
        }, 0);
      }
    }
  }, [isOpen, initialData, isHtmlMode]);

  // Auto-save draft functionality
  useEffect(() => {
    if (!isOpen || (!to && !subject && !bodyHtml)) {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
        setAutoSaveTimer(null);
      }
      return;
    }

    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    // Set new timer to auto-save after 2 seconds of inactivity
    const timer = setTimeout(async () => {
      if (to || subject || bodyHtml) {
        await saveDraft(true);
      }
    }, 2000);

    setAutoSaveTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, bodyHtml, isOpen, draftId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  const saveDraft = async (silent = false) => {
    if (!to && !subject && !bodyHtml) return;

    if (isReadOnly) {
      if (!silent) {
        toast.error('האחסון מלא - לא ניתן לשמור או לערוך טיוטות. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
      }
      return;
    }

    setSaving(true);
    try {
      const draftData = {
        to: to ? to.split(',').map(e => e.trim()).filter(Boolean) : [],
        cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: subject,
        bodyHtml: isHtmlMode ? bodyHtml : undefined,
        bodyText: !isHtmlMode ? bodyHtml : undefined,
        threadId: initialData?.threadId,
      };

      let result;
      if (draftId) {
        result = await updateDraft(draftId, draftData);
      } else {
        result = await createDraft(draftData);
        if (result?.draftId) {
          setDraftId(result.draftId);
        }
      }

      if (!silent) {
        toast.success('טיוטה נשמרה בהצלחה');
      }
    } catch (err: any) {
      console.error('[EmailCompose] Failed to save draft:', err);
      if (!silent) {
        const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
        toast.error('שגיאה בשמירת הטיוטה: ' + errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!to || !to.trim()) {
      toast.error('נא למלא כתובת נמען');
      return;
    }

    if (isReadOnly) {
      toast.error('האחסון מלא - לא ניתן לשלוח או לערוך מיילים');
      return;
    }

    setSending(true);
    try {
      // Delete draft if it exists
      if (draftId) {
        try {
          await deleteDraft(draftId);
        } catch (err) {
          console.warn('[EmailCompose] Failed to delete draft before sending:', err);
        }
      }

      // Convert attachments to base64
      const attachmentsData = attachments.length > 0
        ? await Promise.all(attachments.map(fileToBase64))
        : [];

      const emailData = {
        to: to.split(',').map(e => e.trim()).filter(Boolean),
        cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: subject || '',
        bodyHtml: isHtmlMode ? bodyHtml : undefined,
        bodyText: !isHtmlMode ? bodyHtml : undefined,
        threadId: initialData?.threadId,
        attachments: attachmentsData,
      };

      await sendEmail(emailData);
      toast.success('המייל נשלח בהצלחה');
      
      // Reset form
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBodyHtml('');
      setDraftId(null);
      
      onSent?.();
      onClose();
    } catch (err: any) {
      console.error('[EmailCompose] Failed to send email:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
      toast.error('שגיאה בשליחת המייל: ' + errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendDraft = async () => {
    if (!draftId) {
      await handleSend();
      return;
    }

    if (isReadOnly) {
      toast.error('האחסון מלא - לא ניתן לשלוח או לערוך מיילים. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
      return;
    }

    setSending(true);
    try {
      await sendDraft(draftId);
      toast.success('המייל נשלח בהצלחה');
      
      // Reset form
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBodyHtml('');
      setDraftId(null);
      
      onSent?.();
      onClose();
    } catch (err: any) {
      console.error('[EmailCompose] Failed to send draft:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
      toast.error('שגיאה בשליחת המייל: ' + errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!draftId) {
      onClose();
      return;
    }

    try {
      await deleteDraft(draftId);
      toast.success('הטיוטה נמחקה');
      onClose();
    } catch (err: any) {
      console.error('[EmailCompose] Failed to delete draft:', err);
      toast.error('שגיאה במחיקת הטיוטה: ' + (err.message || 'Unknown error'));
    }
  };

  // File handling
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setAttachments(prev => [...prev, ...newFiles]);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Convert file to base64 for sending
  const fileToBase64 = (file: File): Promise<{ filename: string; data: string; mimeType: string; size: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]; // Remove data:mime;base64, prefix
        resolve({
          filename: file.name,
          data: base64,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-bold text-slate-900">
                {initialData?.replyTo ? 'השב' : 'כתוב הודעה חדשה'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                aria-label="סגור"
              >
                <FiX className="text-slate-600" />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {/* To */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  אל: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  dir="ltr"
                />
              </div>

              {/* Cc/Bcc */}
              <div className="space-y-2">
                <button
                  onClick={() => setShowCc(!showCc)}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  {showCc ? <FiChevronUp /> : <FiChevronDown />}
                  {showCc ? 'הסתר' : 'הצג'} Cc, Bcc
                </button>

                <AnimatePresence>
                  {showCc && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-2 overflow-hidden"
                    >
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Cc:
                        </label>
                        <input
                          type="text"
                          value={cc}
                          onChange={(e) => setCc(e.target.value)}
                          placeholder="example@email.com"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Bcc:
                        </label>
                        <input
                          type="text"
                          value={bcc}
                          onChange={(e) => setBcc(e.target.value)}
                          placeholder="example@email.com"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          dir="ltr"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowCc(false)}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          הסתר
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  נושא:
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="נושא ההודעה"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-slate-700">
                    תוכן:
                  </label>
                  <button
                    onClick={() => {
                      // When switching modes, preserve content
                      const currentContent = isHtmlMode 
                        ? (bodyRef.current?.innerHTML || bodyHtml)
                        : bodyHtml;
                      
                      setIsHtmlMode(!isHtmlMode);
                      
                      // Update the appropriate field based on new mode
                      if (isHtmlMode) {
                        // Switching from HTML to text - extract text only
                        const textContent = bodyRef.current?.innerText || currentContent.replace(/<[^>]*>/g, '');
                        setBodyHtml(textContent);
                      } else {
                        // Switching from text to HTML - preserve as HTML
                        setBodyHtml(currentContent);
                        // Update contentEditable after a brief delay
                        setTimeout(() => {
                          if (bodyRef.current) {
                            isInternalUpdate.current = true;
                            bodyRef.current.innerHTML = currentContent;
                            setTimeout(() => {
                              isInternalUpdate.current = false;
                            }, 0);
                          }
                        }, 0);
                      }
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    {isHtmlMode ? 'מצב HTML' : 'מצב טקסט'}
                  </button>
                </div>
                {isHtmlMode ? (
                  <div
                    ref={bodyRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => {
                      if (!isInternalUpdate.current) {
                        const html = e.currentTarget.innerHTML;
                        setBodyHtml(html);
                      }
                    }}
                    onBlur={(e) => {
                      if (!isInternalUpdate.current) {
                        const html = e.currentTarget.innerHTML;
                        setBodyHtml(html);
                      }
                    }}
                    className="w-full min-h-[300px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-pre-wrap"
                    style={{ direction: 'rtl' }}
                    data-placeholder="כתוב את הודעתך כאן..."
                  />
                ) : (
                  <textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    placeholder="כתוב את הודעתך כאן..."
                    className="w-full min-h-[300px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    dir="rtl"
                  />
                )}
              </div>
              
              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    קבצים מצורפים:
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2 transition-colors"
                  >
                    <FiPaperclip />
                    הוסף קובץ
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {attachments.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FiFile className="text-slate-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 truncate">{file.name}</p>
                            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          aria-label="הסר קובץ"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Add CSS for placeholder in contentEditable */}
              <style>{`
                div[contenteditable][data-placeholder]:empty:before {
                  content: attr(data-placeholder);
                  color: #94a3b8;
                  pointer-events: none;
                }
              `}</style>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSendDraft}
                  disabled={sending || !to.trim() || isReadOnly}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    isReadOnly || sending || !to.trim()
                      ? 'bg-slate-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  title={isReadOnly ? 'האחסון מלא - לא ניתן לשלוח או לערוך מיילים. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.' : ''}
                >
                  <FiSend />
                  {sending ? 'שולח...' : 'שלח'}
                </button>
                <button
                  onClick={() => saveDraft(false)}
                  disabled={saving || isReadOnly}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    isReadOnly || saving
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                  title={isReadOnly ? 'האחסון מלא - לא ניתן לשמור או לערוך טיוטות. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.' : ''}
                >
                  <FiSave />
                  {saving ? 'שומר...' : 'שמור טיוטה'}
                </button>
                {draftId && (
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-2 transition-colors"
                  >
                    <FiTrash2 />
                    מחק
                  </button>
                )}
                {draftId && (
                  <span className="text-xs text-slate-500">טיוטה נשמרה</span>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

